import React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "ui/Avatar";
import { cn } from "util/cn";

/**
 * Visual states for the Instagram-style story ring around an avatar:
 *
 *   - `none`     -> render a plain round avatar with no ring at all.
 *   - `unseen`   -> warm gradient ring, signalling "new story available".
 *   - `seen`     -> muted gray ring, signalling "story exists but the
 *                   viewer has watched everything currently active".
 *
 * The "story exists" signal is owned by the caller (typically via
 * `useActorStoryStatus`); this primitive just paints the ring it is
 * told to paint.
 */
export type AvatarRingState = "none" | "unseen" | "seen";

export interface AvatarRingProps {
  /** Pixel size of the ring's outer edge. The avatar inside is sized to fit. */
  size: number;
  ringState: AvatarRingState;
  /** Resolved image URL. Pass `null`/`undefined` to fall back to initials. */
  imageUrl: string | null | undefined;
  /** Image `alt` text. Should already be localized. */
  imageAlt: string;
  /** 1-2 character initials shown when the image is missing or fails to load. */
  initials: string;
  /** Optional override for the outer `<span>`'s class list. */
  className?: string;
  /**
   * Optional override for the ring color/background classes. Use only
   * when a surface needs a custom ring (e.g. a story-composer add ring).
   */
  ringClassName?: string;
}

/**
 * Padding (in px) between the outer ring and the inner avatar. Matches
 * the visual rhythm Instagram uses (~2-3px depending on size).
 */
const RING_PADDING_PX = 2;

const RING_BACKGROUND_BY_STATE: Record<AvatarRingState, string> = {
  none: "",
  unseen: "bg-gradient-to-br from-fuchsia-500 via-orange-400 to-sky-400",
  seen: "bg-eoe-icon-hover dark:bg-eoe-dark-icon",
};

/**
 * Pure presentational primitive: ring + image + initials fallback. Does
 * no fetching and renders no link wrapper. Compose this with a click
 * target outside the ring (`<ActorAvatar>` does that for the standard
 * story-or-profile contract).
 */
const AvatarRing = React.forwardRef<HTMLSpanElement, AvatarRingProps>(
  (
    { size, ringState, imageUrl, imageAlt, initials, className, ringClassName },
    ref
  ) => {
    const padding = ringState === "none" ? 0 : RING_PADDING_PX;
    const innerSize = Math.max(0, size - padding * 2);
    const ringBackground = ringClassName ?? RING_BACKGROUND_BY_STATE[ringState];

    return (
      <span
        ref={ref}
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center rounded-full",
          ringBackground,
          className
        )}
        style={{
          width: size,
          height: size,
          padding,
        }}
        data-ring-state={ringState}
      >
        {ringState === "none" ? (
          <AvatarInner
            size={innerSize}
            imageUrl={imageUrl}
            imageAlt={imageAlt}
            initials={initials}
          />
        ) : (
          /*
           * The inner ring needs a background-coloured "donut" between
           * the gradient and the avatar so the ring stays crisp on top
           * of any surface (light or dark). Matches `UserStoryAvatar`'s
           * border treatment. Sized via inline style to keep behavior
           * identical to the no-ring branch (see `AvatarInner` for the
           * full cascade explanation).
           */
          <span
            className="flex items-center justify-center overflow-hidden rounded-full bg-eoe-bg dark:bg-eoe-dark-bg"
            style={{ width: innerSize, height: innerSize }}
          >
            <AvatarInner
              size={innerSize}
              imageUrl={imageUrl}
              imageAlt={imageAlt}
              initials={initials}
            />
          </span>
        )}
      </span>
    );
  }
);

AvatarRing.displayName = "AvatarRing";

interface AvatarInnerProps {
  size: number;
  imageUrl: string | null | undefined;
  imageAlt: string;
  initials: string;
}

/**
 * Inner Radix `Avatar` shell. Kept as a separate component so the ring
 * states reuse it without duplicating the image+fallback markup.
 *
 * Sizing notes:
 *   - The Radix `Avatar` Root and the inner `<img>` are both sized via
 *     inline `style` rather than Tailwind utility classes. The global
 *     `img { max-width: 100%; height: auto }` rule in `src/index.css`
 *     is unlayered, and per the CSS cascade, unlayered rules always
 *     beat rules inside `@layer utilities` regardless of selector
 *     specificity. That means `.h-full` on the image silently loses to
 *     the global `height: auto`, the image renders at its natural
 *     size, and `overflow-hidden` becomes the only thing standing
 *     between the layout and a 1024px-wide profile picture spilling
 *     across the page. Inline styles beat unlayered CSS, so we set
 *     dimensions there.
 *   - `display: block` removes the inline-image baseline gap and lets
 *     the wrapper hug the image without an extra pixel of descender
 *     space.
 */
const AvatarInner: React.FC<AvatarInnerProps> = ({
  size,
  imageUrl,
  imageAlt,
  initials,
}) => {
  // Tailwind's `text-*` scale is too coarse for our 24-64px range, so
  // we derive the fallback font size from the avatar size (≈40% of the
  // diameter). Capped between 10px and 22px to avoid extremes.
  const fallbackFontSize = Math.max(10, Math.min(22, Math.round(size * 0.4)));

  return (
    <Avatar
      className="block overflow-hidden rounded-full"
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
    >
      {imageUrl ? (
        <AvatarImage
          src={imageUrl}
          alt={imageAlt}
          className="rounded-full"
          style={{
            width: size,
            height: size,
            display: "block",
            objectFit: "cover",
          }}
        />
      ) : null}
      <AvatarFallback
        className="flex h-full w-full items-center justify-center bg-eoe-bg-hover font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text"
        style={{ fontSize: fallbackFontSize }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

export default AvatarRing;
